import { Col, Row } from "antd";
import { get, set } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAddRoad,
  MdOutlineAlternateEmail,
  MdOutlineCall,
  MdOutlinePinDrop,
  MdOutlineSouthAmerica,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import useZipCodeCities from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  FRANCE_DEPARTMENTS,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Contact = ({ setPercent, isFieldRequired, initialValues, bindField }) => {
  const { t: p } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const COUNTRIES = p("DETAIL.COUNTRIES", { returnObjects: true });
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const zipCodePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`;
  const { cities, loading: citiesLoading } = useZipCodeCities(
    get(initialValues, zipCodePath),
  );

  const streetPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.STREET}.value`;
  const streetField = bindField(streetPath, {
    label: SUBSCRIPTION.FORM.STREET_AND_INDEX.LABEL,
    required: isFieldRequired(streetPath),
    onBlur: () => setPercent(initialValues),
  });

  const addressLocationPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.ADDRESS_LOCATION}.value`;
  const addressLocationField = bindField(addressLocationPath, {
    label: SUBSCRIPTION.FORM.ADDRESS_LOCATION.LABEL,
    onBlur: () => setPercent(initialValues),
  });

  const complementPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`;
  const complementField = bindField(complementPath, {
    label: SUBSCRIPTION.FORM.COMPLEMENT.LABEL,
    onBlur: () => setPercent(initialValues),
  });

  const residencePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.RESIDENCE}.value`;
  const residenceField = bindField(residencePath, {
    label: SUBSCRIPTION.FORM.RESIDENCE.LABEL,
    onBlur: () => setPercent(initialValues),
  });

  const zipCodeField = bindField(zipCodePath, {
    label: SUBSCRIPTION.FORM.POSTAL_CODE.LABEL,
    required: isFieldRequired(zipCodePath),
    disabled: true,
  });

  const cityPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.CITY}.value`;
  const cityField = bindField(cityPath, {
    label: SUBSCRIPTION.FORM.CITY.LABEL,
    required: isFieldRequired(cityPath),
    onBlur: () => setPercent(initialValues),
  });

  const birthCountryPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`;
  const birthCountryField = bindField(birthCountryPath, {
    label: SUBSCRIPTION.FORM.BIRTH_COUNTRY.LABEL,
    required: isFieldRequired(birthCountryPath),
    onChange: (value) => {
      if (value !== "France") {
        set(
          initialValues,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
          "Autre/Etranger",
        );
      }
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  const birthCityPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`;
  const birthCityField = bindField(birthCityPath, {
    label: SUBSCRIPTION.FORM.BIRTH_CITY.LABEL,
    required: isFieldRequired(birthCityPath),
    onBlur: () => setPercent(initialValues),
  });

  const birthDepartmentPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`;
  const birthDepartmentField = bindField(birthDepartmentPath, {
    label: SUBSCRIPTION.FORM.BIRTH_DEPARTMENT.LABEL,
    required: isFieldRequired(birthDepartmentPath),
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const phonePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.PHONE}.value`;
  const phoneField = bindField(phonePath, {
    label: SUBSCRIPTION.FORM.MOBILE.LABEL,
    required: isFieldRequired(phonePath),
    onBlur: () => setPercent(initialValues),
  });

  const landlinePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.LANDLINE}.value`;
  const landlineField = bindField(landlinePath, {
    label: SUBSCRIPTION.FORM.LANDLINE.LABEL,
    onBlur: () => setPercent(initialValues),
  });

  const emailPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.EMAIL}.value`;
  const emailField = bindField(emailPath, {
    label: SUBSCRIPTION.FORM.EMAIL.LABEL,
    required: isFieldRequired(emailPath),
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTACT}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <Form
              type="highlight"
              title={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.ADDRESS.TITLE
              }
            >
              <Row gutter={[16, 16]}>
                <Col lg={24} xs={24}>
                  <FormField
                    path={streetField.path}
                    label={streetField.label}
                    required={streetField.required}
                    disabled={streetField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={streetField.label}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={
                          SUBSCRIPTION.FORM.STREET_AND_INDEX.PLACEHOLDER
                        }
                        type="custom"
                        normalizeText={true}
                        defaultValue={streetField.defaultValue}
                        {...field}
                        onChange={streetField.onChange}
                        onBlur={streetField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={addressLocationField.path}
                    label={addressLocationField.label}
                    required={addressLocationField.required}
                    disabled={addressLocationField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={addressLocationField.label}
                        optional={SUBSCRIPTION.FORM.OPTIONAL}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={
                          SUBSCRIPTION.FORM.ADDRESS_LOCATION.PLACEHOLDER
                        }
                        type="custom"
                        normalizeText={true}
                        defaultValue={addressLocationField.defaultValue}
                        {...field}
                        onChange={addressLocationField.onChange}
                        onBlur={addressLocationField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={complementField.path}
                    label={complementField.label}
                    required={complementField.required}
                    disabled={complementField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={complementField.label}
                        optional={SUBSCRIPTION.FORM.OPTIONAL}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={SUBSCRIPTION.FORM.COMPLEMENT.PLACEHOLDER}
                        type="custom"
                        normalizeText={true}
                        defaultValue={complementField.defaultValue}
                        {...field}
                        onChange={complementField.onChange}
                        onBlur={complementField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={residenceField.path}
                    label={residenceField.label}
                    required={residenceField.required}
                    disabled={residenceField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={residenceField.label}
                        optional={SUBSCRIPTION.FORM.OPTIONAL}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={SUBSCRIPTION.FORM.RESIDENCE.PLACEHOLDER}
                        type="custom"
                        normalizeText={true}
                        defaultValue={residenceField.defaultValue}
                        {...field}
                        onChange={residenceField.onChange}
                        onBlur={residenceField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={zipCodeField.path}
                    label={zipCodeField.label}
                    required={zipCodeField.required}
                    disabled={zipCodeField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={zipCodeField.label}
                        icon={<MdOutlinePinDrop size={16} />}
                        placeholder={SUBSCRIPTION.FORM.POSTAL_CODE.PLACEHOLDER}
                        defaultValue={zipCodeField.defaultValue}
                        {...field}
                        onChange={zipCodeField.onChange}
                        onBlur={zipCodeField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={cityField.path}
                    label={cityField.label}
                    required={cityField.required}
                    disabled={cityField.disabled}
                  >
                    {(field) => (
                      <Select
                        icon={<MdOutlineSouthAmerica size={16} />}
                        label={cityField.label}
                        placeholder={SUBSCRIPTION.FORM.CITY.PLACEHOLDER}
                        theme="light"
                        showSearch
                        options={cities}
                        loading={citiesLoading}
                        defaultValue={cityField.defaultValue}
                        {...field}
                        onChange={cityField.onChange}
                        onBlur={cityField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col lg={24} xs={24}>
            <Form
              type="highlight"
              title={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.BIRTH_PLACE
                  .TITLE
              }
            >
              <Row gutter={[16, 16]}>
                <Col lg={24} xs={24}>
                  <FormField
                    path={birthCountryField.path}
                    label={birthCountryField.label}
                    required={birthCountryField.required}
                    disabled={birthCountryField.disabled}
                  >
                    {(field) => (
                      <Select
                        label={birthCountryField.label}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={
                          SUBSCRIPTION.FORM.BIRTH_COUNTRY.PLACEHOLDER
                        }
                        options={COUNTRIES.map((name) => ({
                          label: name,
                          value: name,
                        }))}
                        showSearch
                        allowClear
                        defaultValue={birthCountryField.defaultValue}
                        {...field}
                        onChange={birthCountryField.onChange}
                        onBlur={birthCountryField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={birthCityField.path}
                    label={birthCityField.label}
                    required={birthCityField.required}
                    disabled={birthCityField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={birthCityField.label}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={SUBSCRIPTION.FORM.BIRTH_CITY.PLACEHOLDER}
                        type="custom"
                        normalizeText={true}
                        defaultValue={birthCityField.defaultValue}
                        {...field}
                        onChange={birthCityField.onChange}
                        onBlur={birthCityField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={birthDepartmentField.path}
                    label={birthDepartmentField.label}
                    required={birthDepartmentField.required}
                    disabled={birthDepartmentField.disabled}
                  >
                    {(field) => (
                      <Select
                        label={birthDepartmentField.label}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={
                          SUBSCRIPTION.FORM.BIRTH_DEPARTMENT.PLACEHOLDER
                        }
                        showSearch
                        allowClear
                        options={FRANCE_DEPARTMENTS.map(({ code, name }) => ({
                          label: `${code && `(${code})`} ${name}`,
                          value: name,
                        }))}
                        value={get(initialValues, birthDepartmentPath)}
                        defaultValue={birthDepartmentField.defaultValue}
                        {...field}
                        onChange={birthDepartmentField.onChange}
                        onBlur={birthDepartmentField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col lg={24} xs={24}>
            <Form
              type="highlight"
              title={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.PHONE.TITLE
              }
              subtitle={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.PHONE.SUBTITLE
              }
            >
              <Row gutter={[16, 16]}>
                <Col lg={24} xs={24}>
                  <Alert
                    showIcon
                    type="info"
                    message={
                      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.PHONE
                        .INFO
                    }
                  />
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={phoneField.path}
                    label={phoneField.label}
                    required={phoneField.required}
                    disabled={phoneField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={phoneField.label}
                        icon={<MdOutlineCall size={16} />}
                        placeholder={SUBSCRIPTION.FORM.MOBILE.PLACEHOLDER}
                        type="custom"
                        stripSpaces={true}
                        defaultValue={phoneField.defaultValue}
                        {...field}
                        onChange={phoneField.onChange}
                        onBlur={phoneField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={landlineField.path}
                    label={landlineField.label}
                    required={landlineField.required}
                    disabled={landlineField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={landlineField.label}
                        optional={SUBSCRIPTION.FORM.OPTIONAL}
                        icon={<MdOutlineCall size={16} />}
                        placeholder={SUBSCRIPTION.FORM.LANDLINE.PLACEHOLDER}
                        type="custom"
                        trim={true}
                        defaultValue={landlineField.defaultValue}
                        {...field}
                        onChange={landlineField.onChange}
                        onBlur={landlineField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={emailField.path}
              label={emailField.label}
              required={emailField.required}
              disabled={emailField.disabled}
            >
              {(field) => (
                <Input
                  label={emailField.label}
                  icon={<MdOutlineAlternateEmail size={16} />}
                  placeholder={SUBSCRIPTION.FORM.EMAIL.PLACEHOLDER}
                  defaultValue={emailField.defaultValue}
                  {...field}
                  onChange={emailField.onChange}
                  onBlur={emailField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <Alert
              showIcon
              type="info"
              message={SUBSCRIPTION.FORM.CONFIRM_CONTACT.INFO_LMF}
            />
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Contact;
